/**
 * CreativeEditor — a person's edit of a draft (or rejected) responsive search
 * ad, inline in the Creative Studio.
 *
 * Every headline (≤ 30 characters) and description (≤ 90) is its own row with a
 * live character counter, rows can be added and removed within Google's RSA
 * limits (3–15 headlines, 2–4 descriptions), plus the two display paths. Lines
 * containing one of the campaign's banned claims are flagged as you type.
 *
 * The server is the authority: the save is the existing PATCH, which refuses a
 * line that does not fit (naming it) or a banned claim (422 BANNED_CLAIM) and
 * otherwise returns the ad to DRAFT — it must be approved again before it can
 * deploy. A person's words are trusted for spelling: no AI pass runs on an
 * edit. Pins on headlines are kept as they were.
 */

import { useId, useState } from 'react';
import type { AdoptCreativeDto, AdoptRsaContentDto } from '@/lib/api';
import { useUpdateAdoptCreative } from '../hooks/useAdvertisingOptimization';
import { findBannedClaim } from '../copy-guidance';
import { RSA_LIMITS } from '../rsa-limits';

type HeadlineRow = { text: string; pinnedPosition?: 1 | 2 | 3 };
type DescriptionRow = { text: string };

const PATH_PATTERN = /^[a-z0-9-]*$/i;

/** Every problem with the draft, as the server would name it; [] = savable. */
export function rsaEditProblems(
  headlines: HeadlineRow[],
  descriptions: DescriptionRow[],
  paths: string[],
  bannedClaims: readonly string[],
): string[] {
  const problems: string[] = [];
  const check = (
    noun: 'Headline' | 'Description',
    items: string[],
    limits: { min: number; max: number; maxChars: number },
  ): void => {
    if (items.length < limits.min || items.length > limits.max) {
      problems.push(
        `An ad needs ${limits.min}–${limits.max} ${noun.toLowerCase()}s; this one has ${items.length}`,
      );
    }
    const seen = new Map<string, number>();
    items.forEach((text, i) => {
      if (!text) {
        problems.push(`${noun} ${i + 1} is empty`);
        return;
      }
      if (text.length > limits.maxChars) {
        problems.push(
          `${noun} ${i + 1} is ${text.length} characters; the limit is ${limits.maxChars}`,
        );
      }
      const first = seen.get(text.toLowerCase());
      if (first !== undefined) {
        problems.push(`${noun} ${i + 1} repeats ${noun.toLowerCase()} ${first + 1}`);
      } else {
        seen.set(text.toLowerCase(), i);
      }
      const claim = findBannedClaim(text, bannedClaims);
      if (claim) problems.push(`${noun} ${i + 1} contains "${claim}", which this campaign bans`);
    });
  };
  check(
    'Headline',
    headlines.map((h) => h.text.trim()),
    RSA_LIMITS.headlines,
  );
  check(
    'Description',
    descriptions.map((d) => d.text.trim()),
    RSA_LIMITS.descriptions,
  );
  paths.forEach((path, i) => {
    const p = path.trim();
    if (p.length > RSA_LIMITS.paths.maxChars) {
      problems.push(`Display path ${i + 1} is over ${RSA_LIMITS.paths.maxChars} characters`);
    } else if (!PATH_PATTERN.test(p)) {
      problems.push(`Display path ${i + 1} may use only letters, digits and hyphens`);
    } else if (p && findBannedClaim(p, bannedClaims)) {
      problems.push(`Display path ${i + 1} contains a banned claim`);
    }
  });
  return problems;
}

function LineRow({
  label,
  value,
  maxChars,
  onChange,
  onRemove,
  canRemove,
  bannedClaims,
  multiline,
}: {
  label: string;
  value: string;
  maxChars: number;
  onChange: (text: string) => void;
  onRemove: () => void;
  canRemove: boolean;
  bannedClaims: readonly string[];
  multiline?: boolean;
}) {
  const counterId = useId();
  const length = value.trim().length;
  const over = length > maxChars;
  const claim = findBannedClaim(value, bannedClaims);
  const inputClass = `flex-1 min-w-0 rounded-md border bg-background px-2 py-1 text-sm ${
    over || claim ? 'border-rose-400 dark:border-rose-500' : 'border-border'
  }`;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-start gap-2">
        {multiline ? (
          <textarea
            aria-label={label}
            aria-describedby={counterId}
            aria-invalid={over || !!claim}
            rows={2}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        ) : (
          <input
            aria-label={label}
            aria-describedby={counterId}
            aria-invalid={over || !!claim}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        )}
        <span
          id={counterId}
          className={`w-14 shrink-0 pt-1 text-right text-xs tabular-nums ${
            over ? 'text-rose-600 dark:text-rose-400 font-medium' : 'text-muted-foreground'
          }`}
        >
          {length}/{maxChars}
        </span>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label={`Remove ${label.toLowerCase()}`}
          className="shrink-0 pt-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
        >
          Remove
        </button>
      </div>
      {claim && (
        <span className="text-xs text-rose-600 dark:text-rose-400">
          Contains “{claim}”, which this campaign’s copy facts ban.
        </span>
      )}
    </div>
  );
}

export function CreativeEditor({
  holderId,
  campaignId,
  creative,
  bannedClaims,
  onDone,
}: {
  holderId: string;
  campaignId: string;
  creative: AdoptCreativeDto;
  /** The campaign's banned claims, for live warnings (the server re-checks). */
  bannedClaims: readonly string[];
  onDone: () => void;
}) {
  const update = useUpdateAdoptCreative(holderId, campaignId);
  const [headlines, setHeadlines] = useState<HeadlineRow[]>(() =>
    creative.content.headlines.map((h) => ({ ...h })),
  );
  const [descriptions, setDescriptions] = useState<DescriptionRow[]>(() =>
    creative.content.descriptions.map((d) => ({ text: d.text })),
  );
  const [paths, setPaths] = useState<string[]>(() => {
    const p = [...(creative.content.paths ?? [])];
    while (p.length < RSA_LIMITS.paths.max) p.push('');
    return p.slice(0, RSA_LIMITS.paths.max);
  });

  const problems = rsaEditProblems(headlines, descriptions, paths, bannedClaims);

  const save = (): void => {
    const content: AdoptRsaContentDto = {
      headlines: headlines.map((h) => ({ ...h, text: h.text.trim() })),
      descriptions: descriptions.map((d) => ({ text: d.text.trim() })),
      paths: paths.map((p) => p.trim().toLowerCase()).filter(Boolean),
      finalUrl: creative.content.finalUrl,
    };
    update.mutate({ creativeId: creative.id, content }, { onSuccess: onDone });
  };

  return (
    <form
      className="flex flex-col gap-4 rounded-md border border-border bg-muted/20 p-3"
      aria-label="Edit ad"
      onSubmit={(e) => {
        e.preventDefault();
        if (problems.length === 0) save();
      }}
    >
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">
          Headlines{' '}
          <span className="font-normal text-muted-foreground">
            ({headlines.length} of {RSA_LIMITS.headlines.min}–{RSA_LIMITS.headlines.max}, up to{' '}
            {RSA_LIMITS.headlines.maxChars} characters each)
          </span>
        </legend>
        {headlines.map((h, i) => (
          <LineRow
            key={i}
            label={`Headline ${i + 1}`}
            value={h.text}
            maxChars={RSA_LIMITS.headlines.maxChars}
            onChange={(text) =>
              setHeadlines(headlines.map((row, j) => (j === i ? { ...row, text } : row)))
            }
            onRemove={() => setHeadlines(headlines.filter((_, j) => j !== i))}
            canRemove={headlines.length > RSA_LIMITS.headlines.min}
            bannedClaims={bannedClaims}
          />
        ))}
        <button
          type="button"
          onClick={() => setHeadlines([...headlines, { text: '' }])}
          disabled={headlines.length >= RSA_LIMITS.headlines.max}
          className="self-start px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
        >
          Add headline
        </button>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">
          Descriptions{' '}
          <span className="font-normal text-muted-foreground">
            ({descriptions.length} of {RSA_LIMITS.descriptions.min}–{RSA_LIMITS.descriptions.max},
            up to {RSA_LIMITS.descriptions.maxChars} characters each)
          </span>
        </legend>
        {descriptions.map((d, i) => (
          <LineRow
            key={i}
            label={`Description ${i + 1}`}
            value={d.text}
            maxChars={RSA_LIMITS.descriptions.maxChars}
            onChange={(text) =>
              setDescriptions(descriptions.map((row, j) => (j === i ? { text } : row)))
            }
            onRemove={() => setDescriptions(descriptions.filter((_, j) => j !== i))}
            canRemove={descriptions.length > RSA_LIMITS.descriptions.min}
            bannedClaims={bannedClaims}
            multiline
          />
        ))}
        <button
          type="button"
          onClick={() => setDescriptions([...descriptions, { text: '' }])}
          disabled={descriptions.length >= RSA_LIMITS.descriptions.max}
          className="self-start px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-50"
        >
          Add description
        </button>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">
          Display paths{' '}
          <span className="font-normal text-muted-foreground">
            (optional, up to {RSA_LIMITS.paths.maxChars} characters: letters, digits, hyphens)
          </span>
        </legend>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {(() => {
              try {
                return new URL(creative.content.finalUrl).host;
              } catch {
                return creative.content.finalUrl;
              }
            })()}
          </span>
          {paths.map((p, i) => (
            <span key={i} className="flex items-center gap-1">
              <span className="text-muted-foreground">/</span>
              <input
                aria-label={`Display path ${i + 1}`}
                value={p}
                onChange={(e) => setPaths(paths.map((x, j) => (j === i ? e.target.value : x)))}
                className="w-32 rounded-md border border-border bg-background px-2 py-1 text-sm"
              />
              <span className="text-xs tabular-nums text-muted-foreground">
                {p.trim().length}/{RSA_LIMITS.paths.maxChars}
              </span>
            </span>
          ))}
        </div>
      </fieldset>

      {problems.length > 0 && (
        <ul
          className="list-disc pl-5 text-xs text-rose-600 dark:text-rose-400"
          aria-label="Fix before saving"
        >
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {update.isError && (
        <div className="text-sm text-rose-600 dark:text-rose-400" role="alert">
          {update.error instanceof Error ? update.error.message : 'Could not save the ad'}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Saving returns the ad to draft — approve it again before it can deploy.
      </p>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={problems.length > 0 || update.isPending}
          className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="px-3 py-1.5 rounded-md border border-border text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
